import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Browser } from '@capacitor/browser';
import apiService from './services/ApiService';
import authService from './services/AuthService';
import timeService from './services/TimeService';
import i18n from './i18n/config';
import Box from '@mui/material/Box';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import VersionCheckManager from './components/VersionCheckManager';
import LoginModal from './components/LoginModal';
import { LoginModalContext } from './context/LoginModalContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdPage from './pages/AdPage';
import AdminPage from './pages/AdminPage';
import GroupsListPage from './pages/GroupsListPage';
import GroupDetailPage from './pages/GroupDetailPage';
import GroupJoinPage from './pages/GroupJoinPage';
import AchievementToast from './components/AchievementToast';
import MobileAppBanner from './components/MobileAppBanner';
import SwipeableMainViews from './components/SwipeableMainViews';
import adMobService from './services/AdMobService';
import useSwipeNavigation from './hooks/useSwipeNavigation';

const FULLSCREEN_ROUTES = ['/login', '/register', '/verify-email', '/forgot-password', '/reset-password'];

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [oauthError, setOauthError] = useState('');
  const [authReady, setAuthReady] = useState(false);
  const loginModalDismissedRef = useRef(false);
  const currentPathRef = useRef(location.pathname);
  const [routesContainerEl, setRoutesContainerEl] = useState(null);

  const { offsetPx, isDragging } = useSwipeNavigation(routesContainerEl);

  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    StatusBar.setStyle({ style: Style.Light });
    adMobService.initialize().then(() => adMobService.prepareInterstitial());
  }, []);

  // Restaurar sesión al arrancar: siempre intentamos obtener un token fresco.
  // El usuario se deriva del JWT, no de localStorage, así que necesitamos el token
  // para saber quién está logado. Si no hay refresh token ni cookie, silentRefresh
  // falla silenciosamente y el usuario aparece como no autenticado.
  useEffect(() => {
    const restoreSession = async () => {
      await authService.silentRefresh();
      const lang = authService.getUser()?.language;
      if (lang) i18n.changeLanguage(lang);
      setAuthReady(true);
    };
    restoreSession();
    timeService.sync();
  }, []);

  const isFullscreen = FULLSCREEN_ROUTES.includes(location.pathname);

  const openLoginModal = (force = false) => {
    if (!force && loginModalDismissedRef.current) return;
    if (force) loginModalDismissedRef.current = false;
    setLoginModalOpen(true);
  };

  const handleOAuthCallback = async (accessToken, error) => {
    if (error) {
      if (error === 'email_not_provided') {
        setOauthError('No se pudo obtener tu email de la cuenta social. Asegúrate de conceder permisos.');
      } else {
        setOauthError('Error al iniciar sesión con la cuenta social. Inténtalo de nuevo.');
      }
      return;
    }
    try {
      apiService.setAccessToken(accessToken);
      const u = await authService.refreshMe();
      if (u?.language) i18n.changeLanguage(u.language);
      setLoginModalOpen(false);
      navigate('/', { replace: true });
    } catch {
      apiService.clearTokens();
      setOauthError('No se pudo obtener el perfil del usuario. Inténtalo de nuevo.');
    }
  };

  // Web: access_token y refresh_token llegan como query params
  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const error = params.get('error');

      if (!accessToken && !error) return;

      window.history.replaceState({}, '', window.location.pathname);
      if (refreshToken) await authService.saveRefreshToken(refreshToken);
      await handleOAuthCallback(accessToken, error);
    };
    run();
  }, [navigate]);

  // Native: botón de retroceso de Android
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = CapApp.addListener('backButton', () => {
      if (currentPathRef.current === '/') {
        CapApp.exitApp();
      } else {
        navigate('/', { replace: true });
      }
    });

    return () => { listener.then(l => l.remove()); };
  }, [navigate]);

  // Native: deep links entrantes (OAuth2 y verificación de email)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = CapApp.addListener('appUrlOpen', async (data) => {
      await Browser.close();
      const url = new URL(data.url);

      // concordare://verify-email?token=...
      if (url.host === 'verify-email') {
        const token = url.searchParams.get('token');
        navigate(`/verify-email?token=${token}`, { replace: true });
        return;
      }

      // concordare://join-group?code=...
      if (url.host === 'join-group') {
        const code = url.searchParams.get('code');
        navigate(`/groups/join?code=${code}`, { replace: true });
        return;
      }

      // concordare://oauth2/callback?access_token=...
      const accessToken = url.searchParams.get('access_token');
      const refreshToken = url.searchParams.get('refresh_token');
      const error = url.searchParams.get('error');
      if (refreshToken) await authService.saveRefreshToken(refreshToken);
      await handleOAuthCallback(accessToken, error);
    });

    return () => { listener.then(l => l.remove()); };
  }, [navigate]);

  if (!authReady) return null;

  return (
    <LoginModalContext.Provider value={openLoginModal}>
      <Box className="app">
        {!isFullscreen && <MobileAppBanner />}
        {!isFullscreen && <Header />}
        <VersionCheckManager />
        <Snackbar
          open={!!oauthError}
          autoHideDuration={6000}
          onClose={() => setOauthError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setOauthError('')}>{oauthError}</Alert>
        </Snackbar>
        <LoginModal
          open={loginModalOpen}
          onClose={() => { loginModalDismissedRef.current = true; setLoginModalOpen(false); }}
          onSuccess={() => { loginModalDismissedRef.current = false; setLoginModalOpen(false); }}
        />
        <AchievementToast />
        <Box
          ref={setRoutesContainerEl}
          sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
          <SwipeableMainViews offsetPx={offsetPx} isDragging={isDragging} />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/" element={null} />
            <Route path="/ad" element={<AdPage />} />
            <Route path="/results" element={null} />
            <Route path="/leaderboard" element={null} />
            {/* Logros y Perfil los renderiza SwipeableMainViews con sesión iniciada;
                aquí solo queda ProtectedRoute para redirigir si se accede sin sesión. */}
            <Route path="/profile" element={<ProtectedRoute>{null}</ProtectedRoute>} />
            <Route path="/history" element={null} />
            <Route path="/achievements" element={<ProtectedRoute>{null}</ProtectedRoute>} />
            <Route path="/groups" element={<ProtectedRoute><GroupsListPage /></ProtectedRoute>} />
            <Route path="/groups/join" element={<ProtectedRoute><GroupJoinPage /></ProtectedRoute>} />
            <Route path="/groups/:groupId" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Box>
    </LoginModalContext.Provider>
  );
}

export default function App() {
  return <AppContent />;
}
